<template>
  <Card :no-card="noCard" no-body>
    <CardInside>
      <template #body>
        <div class="grid gap-6 lg:grid-cols-3">
          <CardButton
            title="Connect Gmail/Google"
            data-id="connections-create-google"
            text="Connect Gmail/Google"
            @click="onClickGoogle"
          >
            <svg class="h-12 w-12" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                fill="#EA4335"
              />
              <path
                d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                fill="#4285F4"
              />
              <path
                d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                fill="#FBBC05"
              />
              <path
                d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                fill="#34A853"
              />
            </svg>
          </CardButton>
          <CardButton
            data-id="connections-create-microsoft"
            text="Connect Microsoft/Exchange"
            title="Connect Microsoft/Exchange"
            @click="onClickMicrosoft"
          >
            <svg
              class="h-12 w-12"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
            >
              <path fill="#F35325" d="M1 1h6.5v6.5H1V1z" />
              <path fill="#81BC06" d="M8.5 1H15v6.5H8.5V1z" />
              <path fill="#05A6F0" d="M1 8.5h6.5V15H1V8.5z" />
              <path fill="#FFBA08" d="M8.5 8.5H15V15H8.5V8.5z" />
            </svg>
          </CardButton>
          <CardButton
            data-id="connections-create-custom"
            text="Connect custom mailbox"
            title="Connect custom mailbox"
            @click="onClickCustom"
          >
            <span class="flex h-12 w-12 items-center">
              <Icon class="text-4xl text-gray-600" icon="customConnector" />
            </span>
          </CardButton>
        </div>
      </template>
      <template #help>
        <p>
          Connect external email accounts to MailSlurp inboxes. Use the
          MailSlurp API and features with your existing email addresses.
        </p>
        <p>
          See
          <NuxtLink
            class="text-link"
            href="https://docs.mailslurp.com/connectors/"
            external
            target="_blank"
            >documentation</NuxtLink
          >
          or
          <NuxtLink class="text-link" to="/connectors">connectors page</NuxtLink
          >.
        </p>
      </template>
    </CardInside>
  </Card>
</template>
<script lang="ts" setup>
import Card from '../../tw/Card.vue';
import CardButton from '../CardButton.vue';
import type { ConnectorConnectionType } from '../../../lib/services/types';
import type { CreateConnectorConnectionParams } from '../../../lib/services/helpers';
import {
  AccountDtoAddonsEnum,
  AppendProductAddonPlanAddonEnum,
  ProviderSettingsMailProviderEnum,
} from '@mailslurp/admin-sdk';
import CardInside from '../../../components/form/CardInside.vue';
import { useStore3 } from '../../../store/main';
import { storeToRefs } from 'pinia';
import { navigateTo } from '#imports';
import { computed } from 'vue';
import { NuxtLink } from '#components';
import Icon from '../../../components/Icon.vue';
import { useNuxtApp } from '#imports';
import { getLogger } from '../../../lib/services/getLogger';
import { combineUrlAndParams } from '../../../lib/services/combineUrlAndParams';
import { getUpgradePageAddonUrl } from '../../../lib/services/upgradeLinks';
const { $captureEvent: captureEvent } = useNuxtApp();

const store3 = useStore3();
const { userAddons } = storeToRefs(store3);
withDefaults(defineProps<{ noCard?: boolean }>(), { noCard: false });
const onClickGoogle = () => goTo(ProviderSettingsMailProviderEnum.GOOGLE);
const onClickMicrosoft = () => goTo(ProviderSettingsMailProviderEnum.MICROSOFT);
const onClickCustom = () => goTo('custom');
const goTo = async (connectorType: ConnectorConnectionType) => {
  await onSelect(connectorType);
};

const log = getLogger('CreateConnectorConnectionsCard');
const onSelect = async (c: ConnectorConnectionType) => {
  captureEvent('click_connector_create', { connectorType: c });
  log('On select ' + c);
  if (!userHasAddon.value) {
    await navigateTo(
      getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.CONNECTORS, {
        expedite: '1',
      }),
    );
  } else {
    const paramsQ: CreateConnectorConnectionParams = {
      connectorType: c,
      create: '1',
    };
    const url = `${combineUrlAndParams(`/connectors/create`, paramsQ)}`;
    await navigateTo(url);
  }
};
const userHasAddon = computed(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.CONNECTORS);
});
</script>
